import { shortDate } from "@/lib/format";
import { colors } from "@/lib/theme";
import type { RsiPoint } from "@/lib/types";
import { useElementWidth } from "@/lib/useElementWidth";

const HEIGHT = 160;
const PAD_TOP = 10;
const PAD_BOTTOM = 18;
const PAD_RIGHT = 28; // 우측 기준선 라벨 공간
const BUY_LINE = 35;
const SELL_LINE = 70;
const MAX_POINTS = 60;

/** RSI(0~100) 추이. 과매수/과매도 기준선 + 영역 채움 + 현재값 포인트. */
export function RsiChart({ points }: { points: RsiPoint[] }) {
  const [ref, width] = useElementWidth();
  const shown = points.filter((p) => p.rsi != null).slice(-MAX_POINTS);

  if (shown.length < 2) {
    return (
      <div className="chart" ref={ref}>
        <p className="hint">RSI 데이터가 부족합니다.</p>
      </div>
    );
  }

  const plotW = Math.max(width - PAD_RIGHT, 1);
  const plotH = HEIGHT - PAD_TOP - PAD_BOTTOM;

  const x = (i: number) => (i / (shown.length - 1)) * plotW;
  const y = (rsi: number) => PAD_TOP + (1 - rsi / 100) * plotH;

  const linePath = shown
    .map(
      (p, i) =>
        `${i === 0 ? "M" : "L"} ${x(i).toFixed(1)} ${y(p.rsi as number).toFixed(1)}`,
    )
    .join(" ");
  const areaPath =
    `${linePath} L ${x(shown.length - 1).toFixed(1)} ${(PAD_TOP + plotH).toFixed(1)}` +
    ` L 0 ${(PAD_TOP + plotH).toFixed(1)} Z`;

  const last = shown[shown.length - 1];
  const lastRsi = last.rsi as number;
  const lastColor =
    lastRsi >= SELL_LINE
      ? colors.sell
      : lastRsi <= BUY_LINE
        ? colors.buy
        : colors.accent;

  return (
    <div className="chart" ref={ref}>
      {width > 0 && (
        <svg
          width={width}
          height={HEIGHT}
          role="img"
          aria-label={`RSI 추이, 현재 ${lastRsi.toFixed(1)}`}
        >
          <defs>
            <linearGradient id="rsiArea" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0" stopColor={colors.accent} stopOpacity={0.28} />
              <stop offset="1" stopColor={colors.accent} stopOpacity={0.02} />
            </linearGradient>
          </defs>

          {/* 기준선: 70(과매수) / 50 / 35(과매도) */}
          <ReferenceLine
            y={y(SELL_LINE)}
            width={plotW}
            label="70"
            color={colors.sell}
          />
          <ReferenceLine
            y={y(50)}
            width={plotW}
            label="50"
            color={colors.cardBorder}
            dashed
          />
          <ReferenceLine
            y={y(BUY_LINE)}
            width={plotW}
            label="35"
            color={colors.buy}
          />

          <path d={areaPath} fill="url(#rsiArea)" />
          <path d={linePath} stroke={colors.accent} strokeWidth={2} fill="none" />
          <circle cx={x(shown.length - 1)} cy={y(lastRsi)} r={4} fill={lastColor} />
        </svg>
      )}

      <div className="chart-axis">
        <span>{shortDate(shown[0].date)}</span>
        <strong style={{ color: lastColor }}>현재 RSI {lastRsi.toFixed(1)}</strong>
        <span>{shortDate(last.date)}</span>
      </div>
    </div>
  );
}

function ReferenceLine({
  y,
  width,
  label,
  color,
  dashed,
}: {
  y: number;
  width: number;
  label: string;
  color: string;
  dashed?: boolean;
}) {
  return (
    <>
      <line
        x1={0}
        y1={y}
        x2={width}
        y2={y}
        stroke={color}
        strokeWidth={1}
        strokeDasharray={dashed ? "4 4" : "6 4"}
        opacity={0.6}
      />
      <text x={width + 4} y={y + 3} fontSize={9} fill={color}>
        {label}
      </text>
    </>
  );
}
